import React, { useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import ManageHistoryRoundedIcon from '@mui/icons-material/ManageHistoryRounded';
import { fmtLocalDateTime, plural } from '../format';
import { PortalAction } from '../types';
import { RESULT_STYLES, userLabel } from '../../audit/auditFormat';
import { DARK, SOFT, SideTitle } from './cardKit';

/** Сколько действий видно сразу; остальные - по кнопке */
const COLLAPSED = 5;
/** Сервер отдаёт не больше этого числа последних действий */
const SERVER_LIMIT = 200;
const RESULT_TITLES: Record<string, string> = { success: 'Успешно', error: 'Ошибка', denied: 'Отказано' };

const ResultBadge: React.FC<{ result: string }> = ({ result }) => {
  const style = RESULT_STYLES[result as keyof typeof RESULT_STYLES] || RESULT_STYLES.error;
  return (
    <Box
      component="span"
      sx={{
        px: 0.75, py: 0.1, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
        color: style.color, backgroundColor: style.background, border: `1px solid ${style.border}`
      }}
    >
      {RESULT_TITLES[result] || result}
    </Box>
  );
};

const ActionRow = React.memo(function ActionRow({ action, first }: { action: PortalAction; first: boolean }) {
  // для отказов и ошибок важна причина, для успешных - что изменилось
  const note = action.result === 'success' ? action.summary : action.message || action.summary;
  return (
    <Box sx={{ py: 1.25, borderTop: first ? 'none' : '1px solid #f1f5f9' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: DARK, lineHeight: 1.3 }}>{action.title}</Typography>
        <ResultBadge result={action.result} />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
        {fmtLocalDateTime(action.createdAt)} • {userLabel(action)}
      </Typography>
      {action.actorUsername && (
        <Tooltip title="Действие выполнено под чужой учётной записью (вход от имени пользователя)" arrow>
          <Typography variant="caption" sx={{ color: '#b45309', display: 'block' }}>Реально: {action.actorUsername}</Typography>
        </Tooltip>
      )}
      {note && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: action.result === 'success' ? 'text.secondary' : '#b45309', bgcolor: SOFT, p: 0.5, borderRadius: 1, wordBreak: 'break-word' }}
        >
          {note}
        </Typography>
      )}
    </Box>
  );
});

/** «Действия в портале»: кто и что делал с документом через портал (журнал действий, без просмотров) */
const PortalActionsBlock: React.FC<{ actions: PortalAction[] }> = ({ actions }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? actions : actions.slice(0, COLLAPSED);
  const hidden = actions.length - visible.length;

  return (
    <>
      <SideTitle
        icon={<ManageHistoryRoundedIcon fontSize="small" />}
        action={actions.length > 0 ? <Typography variant="caption" color="text.secondary">{actions.length}</Typography> : undefined}
      >
        Действия в портале
      </SideTitle>
      {actions.length === 0 && (
        <Typography variant="caption" color="text.secondary">Через портал с документом ничего не делали</Typography>
      )}
      {visible.map((a, idx) => <ActionRow key={a.id} action={a} first={idx === 0} />)}
      {hidden > 0 && (
        <Button size="small" onClick={() => setExpanded(true)} sx={{ textTransform: 'none', mt: 0.5, px: 1 }}>
          Показать ещё {hidden} {plural(hidden, 'действие', 'действия', 'действий')}
        </Button>
      )}
      {expanded && actions.length >= SERVER_LIMIT && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Показаны последние {SERVER_LIMIT} действий. Полная история - в журнале действий.
        </Typography>
      )}
    </>
  );
};

export default React.memo(PortalActionsBlock);
